import {
  ButtonGroup,
  FormErrorMessage,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
} from "@chakra-ui/react";
import { ErrorMessage } from "@hookform/error-message";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import FormInput from "@/common/components/Form/FormInput";
import { FormRootError } from "@/common/components/Form/FormRootError";
import FormSubmit from "@/common/components/Form/FormSubmit";
import ModalCancelButton from "@/common/components/ModalCancelButton";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useCreateAppMutation } from "@/common/data/useCreateAppMutation";
import { useFormMutationSubmitHandler } from "@/common/hooks/useApiForm";
import { CreateAppArgsSchema } from "@reflag/shared/appAPI";
import { EnvironmentUrl } from "@reflag/shared/urls";

const formSchema = CreateAppArgsSchema.pick({
  name: true,
});

export function NewApp({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { orgId } = useEnvironment();
  const createApp = useCreateAppMutation(orgId);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
    },
    shouldUnregister: true,
  });

  const handleSubmit = useFormMutationSubmitHandler(
    form,
    createApp,
    async ({ environments }) => {
      onClose();
      navigate(EnvironmentUrl(environments[0]));
    },
    {
      prepareVariables: (data) => ({
        name: data.name,
      }),
    },
  );

  return (
    <Modal isOpen={isOpen} size="md" onClose={onClose}>
      <ModalOverlay />
      <form onSubmit={handleSubmit}>
        <ModalContent>
          <FormProvider {...form}>
            <ModalHeader>
              <Heading size="lg">New app</Heading>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack align="flex-start" spacing={6}>
                <FormInput
                  autoComplete="off"
                  label="App name"
                  name="name"
                  placeholder="Acme Production"
                  autoFocus
                  data-1p-ignore
                />

                <FormErrorMessage>
                  <ErrorMessage name="production" />
                </FormErrorMessage>

                <FormRootError />
              </VStack>
            </ModalBody>
            <ModalFooter>
              <ButtonGroup>
                <ModalCancelButton shouldNavigate={false} onClick={onClose} />
                <FormSubmit
                  loadingText="Creating..."
                  disableOnSuccess
                  showPostSubmit
                >
                  Create
                </FormSubmit>
              </ButtonGroup>
            </ModalFooter>
          </FormProvider>
        </ModalContent>
      </form>
    </Modal>
  );
}
